import * as Sentry from '@sentry/browser';
import loadable from '@loadable/component';
import { useQuery } from '@tanstack/react-query';
import { useDeviceDetects, useMeta } from 'common/hooks';
import { useUserAccount } from '@tiangong/hooks';
import { Suspense, FC, useEffect, useRef } from 'react';
import { Layout, Spin, Typography } from '@fuxi/eevee-ui';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { LoginInfoContextProvider, useDocumentVisibility } from '@fuxi/eevee-hooks';
import { eventEmitter } from '@tiangong/utils';

import service from 'common/service';
import { IMProvider } from 'common/context/IMContext';
import { usePlatformUserConfig } from 'common/atoms/platformUserConfig';
import { ROUTER_PATHNAME, MyInfoRouterParams } from 'common/router/config';
import { usePublishTaskStartTime } from 'common/atoms/publishTaskStartTime';
import { DefaultPlatformUserRate, DefaultPlatformEnterpriseRate, DefaultPlatformModeRate } from 'common/constants';
import { metaConfig } from '@tiangong/layout/meta.config';

import Root from '@/pages/Root';

import { selectUserInfo } from '@/store/user';
import { useAfterLogin } from '@/hooks/useAfterLogin';
import { useAppSelector } from '@/hooks/useAppSelector';
import { WebsocketProvider } from '@/context/WebsocketContext';
import { EventSourceProvider } from '@/context/EventSourceContext';
import { CustomLoginInfoProvider, useCustomLoginInfoContext } from '@/context/LoginInfoContext';

import { TopLevelPageWrapper } from './TopLevelPageWrapper';
import { SecondaryLevelPageWrapper } from './SecondaryLevelPageWrapper';

import cx from './index.module.css';
import ContractService from '@/pages/ContractService';

const AsyncPage = loadable((props) => import(`../pages/${props.page}/index.tsx`), {
  cacheKey: (props) => props.page,
});

const MySales = loadable(() => import('@/pages/MySales'));
const ShareArtist = loadable(() => import('@/pages/CustomHome/artist/ShareArtist'));
const ArtistList = loadable(() => import('@/pages/ArtistList'));
const CustomHome = loadable(() => import('@/pages/CustomHome'));
const TaskDetail = loadable(() => import('@/pages/TaskDetail'));
const TaskPublishRecommend = loadable(() => import('@/pages/TaskPublishRecommend'));
const PlatformUpdate = loadable(() => import('@/pages/PlatformUpdate'));
const ContractReadig = loadable(() => import('@/pages/ContractReading'));
const ProtocolInfo = loadable(() => import('@/components/ProtocolInfo'));
const AccountNickName = loadable(() => import('@/pages/AccountNickName'));
const AddVersion = loadable(() => import('@/pages/AddVersion'));
const Security = loadable(() => import('@/pages/UserPreference/Security'));
const RealName = loadable(() => import('@/pages/UserPreference/RealName'));
const ArtistAuth = loadable(() => import('@/pages/UserPreference/ArtistAuth'));
const TaskUpdate = loadable(() => import('@/pages/TaskPublish/TaskUpdate'));
const ArtistHomePage = loadable(() => import('@/pages/ArtistHomePage'));
const MediaUserHome = loadable(() => import('@/pages/ArtistHomePage/MediaUserHome'));
const HeaderExternal = loadable(() => import('@/components/HeaderExternal'));
const MessageList = loadable(() => import('@/pages/MessageBoxPage/MessageList'));
const PaymentSetting = loadable(() => import('@/pages/UserPreference/PaymentSetting'));
const MessageSetting = loadable(() => import('@/pages/UserPreference/MessageSetting'));
const TaskPublishSuccess = loadable(() => import('@/pages/TaskPublish/TaskPublishSuccess'));
const AddVersionPreview = loadable(() => import('@/pages/AddVersionPreview'));
const ActivityCenter = loadable(() => import('@/pages/ActivityCenter'));
const PreviewMediaArtworks = loadable(() => import('@/pages/PreviewMediaArtworks'));
const AppDownload = loadable(() => import('@/pages/AppDownload'));
const ExternalWebsite = loadable(() => import('@/pages/ExternalWebsite'));

export const ExternalBasicLayoutWithLogin: FC = () => {
  const navigate = useNavigate();
  const { afterRbacLogin } = useAfterLogin();
  const updateHistoryState = (newLocationHref) => {
    if (newLocationHref) {
      navigate(`${newLocationHref}`);
    } else {
      // 处理下此时 url 不存在的情况
      Sentry.captureException(new Error(`通过 URL 跳转登录，返回了空的URL`));
    }
  };

  return (
    <LoginInfoContextProvider
      customNoAuthRedirectUrl={`/`}
      afterGettingHook={afterRbacLogin}
      cookieDomain=".tiangong.yl.163.com"
      customRouteManager={(newLocationHref) => updateHistoryState(newLocationHref)}>
      <EventSourceProvider>
        <CustomLoginInfoProvider>
          <ExternalBasicLayout />
        </CustomLoginInfoProvider>
      </EventSourceProvider>
    </LoginInfoContextProvider>
  );
};

const ExternalBasicLayout: FC = () => {
  const { pathname } = useLocation();
  const routerPath = pathname.split('/')?.[1];
  const userInfo = useAppSelector(selectUserInfo);
  const { getPlatformPermissionQuery } = useUserAccount();
  const documentVisibility = useDocumentVisibility();
  const { isLogin, isAdmin } = useCustomLoginInfoContext();
  const { setPlatformUserConfig } = usePlatformUserConfig();
  const documentHiddenStartTimeDuringPublishRef = useRef<number>();
  const notInTaskDetail = routerPath !== ROUTER_PATHNAME.VERSION_DETAIL;
  const notInModelLibrary = routerPath !== ROUTER_PATHNAME.MODEL_LIBRARY;
  const notInUpdateProtocol = routerPath !== ROUTER_PATHNAME.PROTOCOL_UPDATE;
  const notInBiddingTaskMarket = routerPath !== ROUTER_PATHNAME.BIDDING_TASKS_MARKET;
  const notInActivityCenter = routerPath !== ROUTER_PATHNAME.ACTIVITY;
  const notInAddVersion = routerPath !== ROUTER_PATHNAME.ADD_VERSION;
  const notInAppDownload = routerPath !== ROUTER_PATHNAME.APP_DOWNLOAD;
  const { publishTaskStartTime, setDocumentHiddenTimeDuringPublish } = usePublishTaskStartTime();
  const notInArtistList = routerPath !== ROUTER_PATHNAME.ARTIST_LIST && routerPath !== ROUTER_PATHNAME.ARTIST_HOME;

  const { selectors, screen } = useDeviceDetects();
  const isMobileDevice = selectors.isMobile && screen.width <= 1199;

  const shouldShowHeader =
    notInTaskDetail && notInAddVersion && (notInActivityCenter || (!notInActivityCenter && !isMobileDevice));

  // 动态往 HTML Head 中注入 meta 标签
  useMeta(metaConfig);

  useQuery({
    queryKey: ['getPlatformUserConfig'],
    queryFn: async () => {
      try {
        const res = await service.userSetting.getPlatformUserConfig();
        setPlatformUserConfig({
          ...res.data,
          platformModeRate: res.data.platformModeRate || DefaultPlatformModeRate,
          platformUserRate: res.data.platformUserRate || DefaultPlatformUserRate,
          platformEnterpriseRate: res.data.platformEnterpriseRate || DefaultPlatformEnterpriseRate,
          sensitiveTimeUserUpdateSwitch: res.data.sensitiveTimeUserUpdateSwitch || false,
          recommendApply: res.data.recommendApply || false,
          socialMediaRead: res.data.socialMediaRead || false,
          settleDisable: res.data.settleDisable || false,
          businessPublishOnly: res.data.businessPublishOnly || false,
        });
        return res.data;
      } catch (error) {}
    },
  });

  useEffect(() => {
    if (documentVisibility === 'hidden' && !!publishTaskStartTime) {
      documentHiddenStartTimeDuringPublishRef.current = Date.now();
    }
    if (documentVisibility === 'visible' && !!publishTaskStartTime) {
      setDocumentHiddenTimeDuringPublish((prev) => {
        return prev + Date.now() - (documentHiddenStartTimeDuringPublishRef.current || 0);
      });
    }
  }, [documentVisibility]);

  useEffect(() => {
    if (
      isLogin &&
      notInActivityCenter &&
      notInModelLibrary &&
      notInArtistList &&
      notInBiddingTaskMarket &&
      notInUpdateProtocol &&
      notInAppDownload
    ) {
      getPlatformPermissionQuery.refetch();
    }
  }, [
    isLogin,
    notInActivityCenter,
    notInModelLibrary,
    notInArtistList,
    notInBiddingTaskMarket,
    notInUpdateProtocol,
    notInAppDownload,
  ]);

  useEffect(() => {
    eventEmitter.setMaxListeners(15);
  }, []);

  return (
    <IMProvider
      shouldIgnoreLogin={
        !isLogin &&
        !(
          notInActivityCenter &&
          notInModelLibrary &&
          notInArtistList &&
          notInBiddingTaskMarket &&
          notInUpdateProtocol &&
          notInAppDownload
        )
      }>
      <WebsocketProvider isLogin={isLogin} userInfo={userInfo}>
        {shouldShowHeader && (
          <Layout className={cx('basic-layout')}>
            <Layout className={cx('right-layout')}>
              <HeaderExternal
                isLogin={isLogin}
                isAdmin={isAdmin}
                shouldShow={
                  isLogin ||
                  (!isLogin &&
                    notInActivityCenter &&
                    notInModelLibrary &&
                    notInBiddingTaskMarket &&
                    notInUpdateProtocol &&
                    notInArtistList &&
                    notInAppDownload)
                }
              />
            </Layout>
          </Layout>
        )}
        <Suspense
          fallback={
            <Layout className={cx('basic-layout')}>
              <Spin spinning={true} tip={<Typography.Text>加载中...</Typography.Text>}>
                <div className={cx('global-spin-empty')}></div>
              </Spin>
            </Layout>
          }>
          <Routes>
            <>
              <Route key="root" path="*" element={<Root />}>
                <Route
                  key={ROUTER_PATHNAME.VERSION_DETAIL}
                  path={`${ROUTER_PATHNAME.VERSION_DETAIL}/:taskId/:versionId`}
                  element={<TaskDetail />}
                />
                <Route
                  key={ROUTER_PATHNAME.ADD_VERSION}
                  path={`${ROUTER_PATHNAME.ADD_VERSION}/:taskId`}
                  element={<AddVersion />}
                />
                <Route
                  key={ROUTER_PATHNAME.USER_PREFERENCE}
                  path={ROUTER_PATHNAME.USER_PREFERENCE}
                  element={<AsyncPage page="UserPreference" />}>
                  <Route index element={<Security />}></Route>
                  <Route path={MyInfoRouterParams.activeTab.security} element={<Security />}></Route>
                  <Route path={MyInfoRouterParams.activeTab.realNameAuth} element={<RealName />}></Route>
                  <Route path={MyInfoRouterParams.activeTab.artistAuth} element={<ArtistAuth />}></Route>
                  <Route path={MyInfoRouterParams.activeTab.paymentSetting} element={<PaymentSetting />}></Route>
                  <Route path={MyInfoRouterParams.activeTab.messageSetting} element={<MessageSetting />}></Route>
                  <Route path={MyInfoRouterParams.activeTab.userProtocol} element={<ProtocolInfo />}></Route>
                </Route>
                <Route
                  key={ROUTER_PATHNAME.BIDDING_TASKS_MARKET}
                  path={`${ROUTER_PATHNAME.BIDDING_TASKS_MARKET}`}
                  element={<AsyncPage page="TaskMarket" />}
                />
                <Route
                  key={ROUTER_PATHNAME.MODEL_LIBRARY}
                  path={ROUTER_PATHNAME.MODEL_LIBRARY}
                  element={<AsyncPage page="ModelLibrary" />}
                />
                <Route
                  key={ROUTER_PATHNAME.ARTIST_HOME}
                  path={ROUTER_PATHNAME.ARTIST_HOME}
                  element={
                    <SecondaryLevelPageWrapper>
                      <ArtistHomePage />
                    </SecondaryLevelPageWrapper>
                  }
                />
                <Route
                  key={ROUTER_PATHNAME.MEDIA_USER_HOME}
                  path={`${ROUTER_PATHNAME.MEDIA_USER_HOME}/:mediaUserId`}
                  element={<MediaUserHome />}
                />
                <Route
                  key={ROUTER_PATHNAME.ACTIVITY}
                  path={`${ROUTER_PATHNAME.ACTIVITY}/*`}
                  element={<ActivityCenter />}
                />
                <Route
                  key={ROUTER_PATHNAME.APP_DOWNLOAD}
                  path={`${ROUTER_PATHNAME.APP_DOWNLOAD}`}
                  element={<AppDownload />}
                />
                {isLogin && (
                  <>
                    <Route
                      key={ROUTER_PATHNAME.MODEL_LIBRARY}
                      path={`${ROUTER_PATHNAME.MODEL_LIBRARY}/:fileId`}
                      element={<AsyncPage page="Download" />}
                    />
                    <Route
                      key={ROUTER_PATHNAME.SETTING}
                      path={`${ROUTER_PATHNAME.SETTING}/*`}
                      element={<AsyncPage page="Settings" />}
                    />
                  </>
                )}
                <Route
                  key={ROUTER_PATHNAME.ARTIST_LIST}
                  path={`${ROUTER_PATHNAME.ARTIST_LIST}`}
                  element={
                    <TopLevelPageWrapper>
                      <ArtistList />
                    </TopLevelPageWrapper>
                  }
                />
                {/*分享创作者列表*/}
                <Route
                  key={ROUTER_PATHNAME.SHARE_ARTIST}
                  path={`${ROUTER_PATHNAME.SHARE_ARTIST}`}
                  element={
                    <SecondaryLevelPageWrapper>
                      <ShareArtist />
                    </SecondaryLevelPageWrapper>
                  }
                />
                <Route
                  key={ROUTER_PATHNAME.TASK_UPDATE}
                  path={`${ROUTER_PATHNAME.TASK_UPDATE}/:parentTaskId/:projectCode`}
                  element={<TaskUpdate />}
                />
                <Route
                  key={ROUTER_PATHNAME.TASK_PUBLISH}
                  path={`${ROUTER_PATHNAME.TASK_PUBLISH}/:projectId/:projectCode`}
                  element={<TaskPublishRecommend />}
                />
                <Route
                  key={ROUTER_PATHNAME.TASK_PUBLISH_SUCCESS}
                  path={`${ROUTER_PATHNAME.TASK_PUBLISH_SUCCESS}/:parentTaskId`}
                  element={<TaskPublishSuccess />}
                />
                <Route
                  key={ROUTER_PATHNAME.PREVIEW_MEDIA_ARTWORKS}
                  path={`${ROUTER_PATHNAME.PREVIEW_MEDIA_ARTWORKS}/:mediaUserId`}
                  element={<PreviewMediaArtworks />}
                />
                <Route
                  key={ROUTER_PATHNAME.MESSAGE_BOX}
                  path={ROUTER_PATHNAME.MESSAGE_BOX}
                  element={<AsyncPage page="MessageBoxPage" />}>
                  <Route index element={<MessageList activeMessageType={'message'} />} />
                  <Route path={'message'} element={<MessageList activeMessageType={'message'} />} />
                  <Route path={'notice'} element={<MessageList activeMessageType={'notice'} />} />
                </Route>
                <Route path={ROUTER_PATHNAME.ARTIST_AUTH} element={<AsyncPage page="ArtistAuthPage" />} />
                {/*<Route path={`${ROUTER_PATHNAME.MY_SALES}`} element={<AsyncPage page="MySales" />} />*/}
                <Route path={`${ROUTER_PATHNAME.MY_SALES}`} element={<MySales />} />
                <Route
                  path={`${ROUTER_PATHNAME.CONTRACT_READING}/:contractTemplateId/:contractName`}
                  element={<ContractReadig />}
                />
                <Route path="*" element={<CustomHome />} />
                <Route
                  key={ROUTER_PATHNAME.PREVIEW_MODEL}
                  path={ROUTER_PATHNAME.PREVIEW_MODEL}
                  element={<AddVersionPreview />}
                />
              </Route>
            </>

            <Route
              path={ROUTER_PATHNAME.NICKNAME_SETTING}
              key={ROUTER_PATHNAME.NICKNAME_SETTING}
              element={<AccountNickName />}
            />
            <Route
              path={ROUTER_PATHNAME.PLATFORM_UPDATE}
              key={ROUTER_PATHNAME.PLATFORM_UPDATE}
              element={<PlatformUpdate />}
            />
            <Route
              path={ROUTER_PATHNAME.EXTERNAL_WEBSITE}
              key={ROUTER_PATHNAME.EXTERNAL_WEBSITE}
              element={
                <SecondaryLevelPageWrapper>
                  {' '}
                  <ExternalWebsite />{' '}
                </SecondaryLevelPageWrapper>
              }
            />
            <Route
              path={ROUTER_PATHNAME.CONTRACT_SERVICE}
              key={ROUTER_PATHNAME.CONTRACT_SERVICE}
              element={<ContractService />}
            />
          </Routes>
        </Suspense>
      </WebsocketProvider>
    </IMProvider>
  );
};
